import React, { useState } from "react";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import Countdown from "@/components/common/Countdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Flame, Play, Save, RefreshCw } from "lucide-react";

const MILES = [
    ["24h", "24 Hours Before"], ["6h", "6 Hours Before"], ["1h", "1 Hour Before"],
    ["start", "Purge Start"], ["end", "Purge End"],
];

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function Automation() {
    const { data, reload } = useFetch("/admin/purge-schedules");
    const [edits, setEdits] = useState({});
    const [busy, setBusy] = useState(false);
    if (!data) return <Loader />;

    const getVal = (s, field) => (edits[s.id]?.[field] !== undefined ? edits[s.id][field] : s[field]);
    const setVal = (s, field, v) => setEdits({ ...edits, [s.id]: { ...edits[s.id], [field]: v } });

    const save = async (s) => {
        const start = getVal(s, "start");
        const end = getVal(s, "end");
        const enabled = getVal(s, "enabled");
        const notifications = getVal(s, "notifications") || s.notifications;
        try {
            await api.post("/admin/purge-schedules", {
                community_key: s.community_key,
                start: new Date(start).toISOString(),
                end: new Date(end).toISOString(),
                enabled, notifications, reset_fired: true,
            });
            toast.success("Purge schedule saved", { description: s.community_name });
            setEdits({ ...edits, [s.id]: undefined });
            reload();
        } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || "" }); }
    };

    const runNow = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post("/admin/purge-run");
            toast.success("Purge check complete", { description: r.fired.length ? `${r.fired.length} alert(s) fired` : "No milestones due right now" });
            reload();
        } catch { toast.error("Run failed"); } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-automation">
            <PageHeader title="Automation" subtitle="End-of-month purge schedules. Alerts fire automatically every 15 min via platform cron."
                action={<Button data-testid="purge-run-now" disabled={busy} onClick={runNow} className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90"><Play size={15} className="mr-1" /> Run Check Now</Button>} />

            <div className="space-y-5">
                {data.length === 0 && <div className="text-muted-foreground">No purge schedules. Only purge-enabled communities appear here.</div>}
                {data.map((s) => {
                    const notifs = getVal(s, "notifications") || s.notifications || {};
                    const firedKeys = Object.keys(s.fired || {});
                    return (
                        <SectionCard key={s.id} testId={`purge-schedule-${s.community_key}`}
                            title={<span className="flex items-center gap-2"><Flame size={18} className="text-danger" /> {s.community_name}</span>}
                            action={<StatusBadge status={getVal(s, "enabled") ? "active" : "offline"} label={getVal(s, "enabled") ? "ENABLED" : "DISABLED"} />}>
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                                <div>
                                    <label className="label-mono text-muted-foreground">Purge Start</label>
                                    <Input data-testid={`purge-start-${s.community_key}`} type="datetime-local"
                                        value={toLocalInput(getVal(s, "start"))} onChange={(e) => setVal(s, "start", new Date(e.target.value).toISOString())}
                                        className="bg-background border-border mt-1" />
                                </div>
                                <div>
                                    <label className="label-mono text-muted-foreground">Purge End</label>
                                    <Input data-testid={`purge-end-${s.community_key}`} type="datetime-local"
                                        value={toLocalInput(getVal(s, "end"))} onChange={(e) => setVal(s, "end", new Date(e.target.value).toISOString())}
                                        className="bg-background border-border mt-1" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={getVal(s, "enabled")} onCheckedChange={(v) => setVal(s, "enabled", v)} data-testid={`purge-enabled-${s.community_key}`} />
                                    <span className="label-mono text-muted-foreground">Enabled</span>
                                </div>
                            </div>

                            <div className="mt-4 rounded-md border border-border bg-background/40 p-3">
                                <Countdown target={getVal(s, "start")} label="Countdown to Purge" danger testId={`purge-countdown-admin-${s.community_key}`} />
                            </div>

                            <div className="mt-4">
                                <div className="label-mono text-muted-foreground mb-2">Notification Schedule (Dashboard · Discord · Push)</div>
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    {MILES.map(([k, label]) => {
                                        const fired = firedKeys.includes(k);
                                        return (
                                            <label key={k} className="flex flex-col gap-1 rounded-md border border-border bg-background/40 p-2 cursor-pointer">
                                                <div className="flex items-center justify-between">
                                                    <span className="label-mono text-muted-foreground">{label}</span>
                                                    <Switch data-testid={`notif-${s.community_key}-${k}`} checked={notifs[k] !== false}
                                                        onCheckedChange={(v) => setVal(s, "notifications", { ...notifs, [k]: v })} />
                                                </div>
                                                {fired && <span className="label-mono text-radioactive">FIRED</span>}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <Button data-testid={`purge-save-${s.community_key}`} onClick={() => save(s)} className="mt-4 font-heading uppercase bg-hazard text-black hover:bg-hazard/90">
                                <Save size={14} className="mr-1" /> Save Schedule
                            </Button>
                        </SectionCard>
                    );
                })}
            </div>
            <WipeSection />
            <Footer />
        </div>
    );
}

function WipeSection() {
    const { data, reload } = useFetch("/admin/wipe-schedules");
    const [edits, setEdits] = useState({});
    const [busy, setBusy] = useState(false);
    if (!data) return null;
    const getVal = (s, f) => (edits[s.id]?.[f] !== undefined ? edits[s.id][f] : s[f]);
    const setVal = (s, f, v) => setEdits({ ...edits, [s.id]: { ...edits[s.id], [f]: v } });

    const save = async (s) => {
        try {
            await api.post("/admin/wipe-schedules", {
                community_key: s.community_key,
                save_wipe: new Date(getVal(s, "save_wipe")).toISOString(),
                blueprint_wipe: getVal(s, "blueprint_wipe") ? new Date(getVal(s, "blueprint_wipe")).toISOString() : null,
                frequency: getVal(s, "frequency"), notes: getVal(s, "notes") || "",
                enabled: getVal(s, "enabled"), notifications: getVal(s, "notifications") || s.notifications, reset_fired: true,
            });
            toast.success("Wipe schedule saved", { description: s.community_name });
            setEdits({ ...edits, [s.id]: undefined }); reload();
        } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || "" }); }
    };
    const runNow = async () => {
        setBusy(true);
        try { const { data: r } = await api.post("/admin/wipe-run"); toast.success("Wipe check complete", { description: r.fired.length ? `${r.fired.length} alert(s) fired` : "No wipe milestones due right now" }); reload(); }
        catch { toast.error("Run failed"); } finally { setBusy(false); }
    };

    return (
        <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-wide flex items-center gap-2"><RefreshCw size={20} className="text-hazard" /> Wipe Scheduler</h2>
                <Button data-testid="wipe-run-now" disabled={busy} onClick={runNow} className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90"><Play size={15} className="mr-1" /> Run Check Now</Button>
            </div>
            <div className="space-y-5">
                {data.map((s) => {
                    const notifs = getVal(s, "notifications") || s.notifications || {};
                    const firedKeys = Object.keys(s.fired || {});
                    return (
                        <SectionCard key={s.id} testId={`wipe-schedule-${s.community_key}`}
                            title={<span className="flex items-center gap-2"><RefreshCw size={18} className="text-hazard" /> {s.community_name}</span>}
                            action={<StatusBadge status={getVal(s, "enabled") ? "active" : "offline"} label={getVal(s, "enabled") ? `ENABLED · ${getVal(s, "frequency")?.toUpperCase()}` : "DISABLED"} />}>
                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
                                <div>
                                    <label className="label-mono text-muted-foreground">Save Wipe</label>
                                    <Input data-testid={`wipe-save-date-${s.community_key}`} type="datetime-local" value={toLocalInput(getVal(s, "save_wipe"))}
                                        onChange={(e) => setVal(s, "save_wipe", new Date(e.target.value).toISOString())} className="bg-background border-border mt-1" />
                                </div>
                                <div>
                                    <label className="label-mono text-muted-foreground">Blueprint Wipe</label>
                                    <Input type="datetime-local" value={toLocalInput(getVal(s, "blueprint_wipe"))}
                                        onChange={(e) => setVal(s, "blueprint_wipe", new Date(e.target.value).toISOString())} className="bg-background border-border mt-1" />
                                </div>
                                <div>
                                    <label className="label-mono text-muted-foreground">Frequency</label>
                                    <Input data-testid={`wipe-frequency-${s.community_key}`} value={getVal(s, "frequency")} onChange={(e) => setVal(s, "frequency", e.target.value)} className="bg-background border-border mt-1" placeholder="Weekly / Biweekly / Monthly" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={getVal(s, "enabled")} onCheckedChange={(v) => setVal(s, "enabled", v)} data-testid={`wipe-enabled-${s.community_key}`} />
                                    <span className="label-mono text-muted-foreground">Enabled</span>
                                </div>
                            </div>
                            <div className="mt-4 rounded-md border border-border bg-background/40 p-3">
                                <Countdown target={getVal(s, "save_wipe")} label="Countdown to Save Wipe" testId={`wipe-countdown-${s.community_key}`} />
                            </div>
                            <div className="mt-4">
                                <div className="label-mono text-muted-foreground mb-2">Wipe Alerts (Dashboard · Discord · Push)</div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[["24h", "24 Hours Before"], ["1h", "1 Hour Before"], ["now", "Wipe Live"]].map(([k, label]) => (
                                        <label key={k} className="flex flex-col gap-1 rounded-md border border-border bg-background/40 p-2 cursor-pointer">
                                            <div className="flex items-center justify-between">
                                                <span className="label-mono text-muted-foreground">{label}</span>
                                                <Switch data-testid={`wipe-notif-${s.community_key}-${k}`} checked={notifs[k] !== false} onCheckedChange={(v) => setVal(s, "notifications", { ...notifs, [k]: v })} />
                                            </div>
                                            {firedKeys.includes(k) && <span className="label-mono text-radioactive">FIRED</span>}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <Input value={getVal(s, "notes") || ""} onChange={(e) => setVal(s, "notes", e.target.value)} className="bg-background border-border mt-3" placeholder="Notes (optional)" />
                            <Button data-testid={`wipe-save-${s.community_key}`} onClick={() => save(s)} className="mt-4 font-heading uppercase bg-hazard text-black hover:bg-hazard/90"><Save size={14} className="mr-1" /> Save Schedule</Button>
                        </SectionCard>
                    );
                })}
            </div>
        </div>
    );
}
